/*
 * Stat highlights count-up.
 *
 * Progressive enhancement over server-rendered numbers: the final value is already
 * in the DOM (so no-JS visitors and assistive tech read the real figure). When the
 * section scrolls into view this animates each value from 0 up to its target once,
 * then stops observing.
 *
 * Honours prefers-reduced-motion: reduce — in that case nothing is touched and the
 * static server-rendered values stand. Prefix/suffix (units, "<", "+") live outside
 * the animated span in the Liquid, so only the digits move.
 */
if (!customElements.get('stat-counter')) {
  customElements.define(
    'stat-counter',
    class StatCounter extends HTMLElement {
      connectedCallback() {
        this.values = Array.from(this.querySelectorAll('[data-stat-target]'));
        if (!this.values.length) return;

        // Respect the user's motion preference — leave the real numbers as rendered.
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.duration = parseInt(this.dataset.duration, 10) || 1600;

        // Reset to the start of the count only once we know we'll animate.
        this.values.forEach((el) => {
          const decimals = parseInt(el.dataset.statDecimals, 10) || 0;
          el.textContent = (0).toFixed(decimals);
        });

        this.animate = this.animate.bind(this);

        if (!('IntersectionObserver' in window)) {
          this.run();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.run();
                this.observer.disconnect();
              }
            });
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      run() {
        this.start = null;
        this.frame = requestAnimationFrame(this.animate);
      }

      animate(now) {
        if (this.start === null) this.start = now;
        // easeOutCubic — quick then settles, so the final figure is legible.
        const raw = Math.min((now - this.start) / this.duration, 1);
        const eased = 1 - Math.pow(1 - raw, 3);

        this.values.forEach((el) => {
          const target = parseFloat(el.dataset.statTarget) || 0;
          const decimals = parseInt(el.dataset.statDecimals, 10) || 0;
          el.textContent = (target * eased).toFixed(decimals);
        });

        if (raw < 1) {
          this.frame = requestAnimationFrame(this.animate);
        }
      }
    }
  );
}
